import dayjs, { Dayjs } from "dayjs";

/**
 * Pure helpers for the Staff Profile Weekly/Calendar planner — kept out of the
 * component so grid building, event grouping, de-duplication and the consistent
 * colour mapping are unit testable without React.
 */

export const WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
export const DAY_LABEL: Record<string, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

export interface CalendarDayCell {
  date: Dayjs;
  iso: string; // YYYY-MM-DD
  inMonth: boolean;
  isToday: boolean;
}

/**
 * A month grid of weeks (Sunday-first), each a row of 7 day cells. Trailing
 * weeks that are entirely in the next month are dropped so a 5-week month
 * doesn't render an empty 6th row.
 */
export const buildMonthGrid = (month: Dayjs, today: Dayjs = dayjs()): CalendarDayCell[][] => {
  const startOfMonth = month.startOf("month");
  let cursor = startOfMonth.startOf("week"); // Sunday of the first week
  const weeks: CalendarDayCell[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: CalendarDayCell[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        date: cursor,
        iso: cursor.format("YYYY-MM-DD"),
        inMonth: cursor.month() === month.month(),
        isToday: cursor.isSame(today, "day"),
      });
      cursor = cursor.add(1, "day");
    }
    weeks.push(week);
  }
  // Drop a final week that is wholly outside the month.
  while (weeks.length > 4 && weeks[weeks.length - 1].every((c) => !c.inMonth)) {
    weeks.pop();
  }
  return weeks;
};

/** Group calendar events by their ISO date, each list sorted by start time. */
export const groupEventsByDate = (events: any[]): Record<string, any[]> => {
  const map: Record<string, any[]> = {};
  (events || []).forEach((e) => {
    const k = String(e?.date ?? "").slice(0, 10);
    if (!k) return;
    (map[k] = map[k] || []).push(e);
  });
  Object.values(map).forEach((list) =>
    list.sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")))
  );
  return map;
};

// ── Consistent colour mapping (never random) ──
const SUBJECT_COLORS: Record<string, string> = {
  mathematics: "#6366f1", math: "#6366f1", maths: "#6366f1",
  english: "#0ea5e9",
  science: "#22c55e",
  data: "#f59e0b",
};
const FALLBACK_COLORS = ["#8b5cf6", "#ec4899", "#14b8a6", "#ef4444", "#0ea5e9", "#f59e0b", "#22c55e", "#6366f1"];

export const subjectColor = (name?: string): string => {
  const key = (name || "").trim().toLowerCase();
  if (SUBJECT_COLORS[key]) return SUBJECT_COLORS[key];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = key.charCodeAt(i) + ((h << 5) - h);
  return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length];
};

/** Status overrides subject colour (cancelled/completed read as state). */
export const statusColor = (status?: string): string => {
  switch ((status || "").toLowerCase()) {
    case "cancelled": return "#ef4444";
    case "completed": return "#64748b";
    default: return "";
  }
};

export const eventColor = (ev: any): string =>
  statusColor(ev?.status) || subjectColor((ev?.subjects && ev.subjects[0]) || "");

/** "Grade 1 · Science" style label for an event/session. */
export const eventTitle = (ev: any): string => {
  const grades = (ev?.grades || []).join(", ");
  const subjects = (ev?.subjects || []).join(", ");
  return [grades, subjects].filter(Boolean).join(" · ") || "Class";
};

/**
 * De-duplicate recurring week slots on a stable key (class + start time) so the
 * same recurring class never appears twice on one weekday.
 */
export const dedupWeekSlots = (slots: any[]): any[] => {
  const seen = new Set<string>();
  return (slots || []).filter((s) => {
    const k = `${s?.class_id ?? s?.name}|${s?.start_time}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/** Build the 7-day planner rows from the recurring weekly_schedule map. */
export const buildWeekPlanner = (weekly: Record<string, any[]>): { key: string; label: string; slots: any[] }[] =>
  WEEK_DAYS.map((d) => {
    const slots = dedupWeekSlots((weekly?.[d] ?? []).slice()).sort((a, b) =>
      String(a.start_time || "").localeCompare(String(b.start_time || ""))
    );
    return { key: d, label: DAY_LABEL[d], slots };
  });

/**
 * Keep only weekdays the teacher actually has classes on, so the weekly grid
 * never renders empty Saturday/Sunday columns. Order (Mon→Sun) is preserved
 * from buildWeekPlanner.
 */
export const activeWeekDays = <T extends { slots: any[] }>(planner: T[]): T[] =>
  (planner || []).filter((d) => (d?.slots?.length ?? 0) > 0);

/** Time label like "8:00 AM–9:00 AM". */
export const fmtTime = (t?: string): string => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(d);
};
export const timeRange = (s?: string, e?: string): string => [fmtTime(s), fmtTime(e)].filter(Boolean).join("–");
